import React, { PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react';
import SimpleModal from 'components/SimpleModal';
import ProcessingButton from 'components/ProcessingButton';
import Linkable from 'components/Linkable';
import Translation from 'utils/Language/Translation';
import { distinct, run, sleep } from 'component_utils/utils';
import RoleProtected from 'components/RoleProtected';
import { useConfig } from 'utils/Config';
import { beTableAsComponent, Column } from 'components/BeTable';
import Item from './item';
import Location from './location';
import Batch from './batch';
import Serial from './serial';
import { useApi } from 'utils/API';
import { IInformationObjectsCustomization, IInformationObjectsCustomization$ICustomDataGrid } from 'interfaces';
import { Tab } from 'components/uncontrolled_tabs';
import { isNil } from 'lodash';
import Date, { DATE, DATE_TIME } from 'components/Date';

type IProps = PropsWithChildren<{
  hasLoaded: any;
  inline: boolean;
  copyValue: () => string;
  description: () => ReactNode;
  detailedRender: () => ReactNode;
  dialog: {
    closeBtnText: ReactNode;
    titleText: ReactNode;
    content: () => ReactNode;
  };
}>;

export function useCustomTabs(object: keyof IInformationObjectsCustomization, code: string) {
  const api = useApi()
  const config = useConfig()
  const [customTabsData, setCustomTabsData] = useState<{[k:number]: any[]}>({})
  useEffect(() => {
    setCustomTabsData({})
  }, [code])
  const customTabs = useMemo(() => {
    return (config.customization[object] as IInformationObjectsCustomization$ICustomDataGrid[]).map((grid, idx) => {
      const T = beTableAsComponent<any>({
        ignoreTableIdCheck: true,
        tableId: 'custom-'+object+'-table-'+idx,
        getRowClasses(args) {
          if (grid.rowClassesColumn) {
            return (args.row[grid.rowClassesColumn] || "").split(",").map((it: string) => it.trim()).filter((it: string) => it)
          } 
          return []
        },
        columns: grid.columns.map((col, colIdx) => {
          function applyAggregates(template: string, context: any) {
            const ops: {[k:string]: (arr: any[]) => any} = {
              sum: arr => arr.reduce((a, b) => a + b, 0),
              min: arr => Math.min(...arr),
              max: arr => Math.max(...arr),
              count: arr => arr.length,
              count_distinct: arr => distinct(arr).length
            };

            return template.replace(/\{(\w+):(\w+)\}/g, (match, key, operation) => {
              const values = context[key];
              if (!values) return match;           // unknown variable
              if (!ops[operation]) return match;   // unknown operation
              return ops[operation](values);
            });
          }

          const baseColumn = run<Column<any, {}, {}>>(() => {
            if (col.referenceToObject === 'items') {
              return {
                Header: col.label,
                value: (row) => row[col.name]?.visualCode + ' / ' + row[col.name]?.name,
                Cell: ({ row }) => <Item inline obj={row[col.name]}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]?.code) }) : undefined,
                filterable: true,
              }
            }
            
            if (col.referenceToObject === 'binLocations') {
              return {
                Header: col.label,
                value: (row) => row[col.name]?.visualCode,
                Cell: ({ row }) => <Location inline obj={row[col.name]}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]?.code) }) : undefined,
                filterable: true,
              }
            }

            if (col.referenceToObject === 'batches') {
              return {
                Header: col.label,
                value: (row) => row[col.name]?.visualCode,
                Cell: ({ row }) => <Batch inline obj={row[col.name]}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]?.code) }) : undefined,
                filterable: true,
              }
            }

            if (col.referenceToObject === 'serials') {
              return {
                Header: col.label,
                value: (row) => row[col.name]?.visualCode,
                Cell: ({ row }) => <Serial inline obj={row[col.name]}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]?.code) }) : undefined,
                filterable: true,
              }
            }

            if (col.referenceToObject === 'date') {
              return {
                Header: col.label,
                value: (row) => row[col.name],
                Cell: ({ row }) => <Date val={row[col.name]} format={DATE}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]) }) : undefined,
              }
            }

            if (col.referenceToObject === 'datetime') {
              return {
                Header: col.label,
                value: (row) => row[col.name],
                Cell: ({ row }) => <Date val={row[col.name]} format={DATE_TIME}/>,
                Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]) }) : undefined,
              }
            }

            return {
              Header: col.label,
              filterable: true,
              value: (row) => row[col.name] || '',
              Footer: col.footer ? ({ rows }) => applyAggregates(col.footer, { values: rows.map(it => it[col.name]) }) : undefined,
            }
          })

          // set extra fields
          baseColumn.className = col.classes;
          return baseColumn
        })
      })

      return (data: any[]) => (
        <Tab key={idx} name={grid.label} onFirstOpen={async () => {
          const d = await api.getCustomGridForObjectInformation('items', idx, code)
          setCustomTabsData(old => ({
            ...old,
            [idx]: d
          }))
        }}>
          <T data={data || []} loading={isNil(data)}/>
        </Tab>
      )
    })
  }, [object, config, api, code])

  return customTabs.map((ct, ctidx) => ct(customTabsData[ctidx]))
}

export default ({ hasLoaded, inline, copyValue, description, detailedRender, dialog }: IProps) => {
  if (!hasLoaded) {
    return null;
  } else if (inline) {
    return (
      <div style={{ display: 'inline-block' }}>
        <SimpleModal
          title={dialog.titleText}
          fullscreen={true}
          trigger={(open) => <Linkable onOpen={open}>{description()}</Linkable>}
          buttons={(close) => [
            <RoleProtected roles={['authCopyObjectCodes']}>
              <ProcessingButton
                color='tertiary'
                onClick={async () => { 
                  navigator.clipboard.writeText(copyValue())
                  await sleep(100)
                }}
                key={'copy'}
              >
                <Translation name="T.misc.copy"/>
              </ProcessingButton>
            </RoleProtected>,
            <ProcessingButton
              color={'primary'}
              onClick={close}
              data-testid={'simple-modal-primary'}
              key={'close'}
            >
              {dialog.closeBtnText}
            </ProcessingButton>,
          ]}
        >
          {dialog.content()}
        </SimpleModal>
      </div>
    );
  } else {
    return (
      <div className="flex-container flex-fill-height">
        <div className="flex-fill-height">{detailedRender()}</div>
      </div>
    );
  }
};
